"use client";

import { useState, useMemo } from "react";
import { useHallOfFameCategories } from "@/api/hook/useHallOfFame";
import { HallOfFameCategoryCard } from "@/components/hall-of-fame/HallOfFameCategoryCard";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";

export default function HallOfFameListingPage() {
  const { data: categories, isLoading, error } = useHallOfFameCategories();
  const [searchQuery, setSearchQuery] = useState("");

  const filtered = useMemo(() => {
    if (!categories) return [];
    const q = searchQuery.toLowerCase().trim();
    if (!q) return categories;
    return categories.filter((c) => c.title.toLowerCase().includes(q));
  }, [categories, searchQuery]);

  console.log(filtered);
  return (
    <main>
      <Header />

      {/* ═══════════════════════════════════════ HERO ═══════════════════════════ */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden bg-[#21205A]">
        {/* Video background */}
        <video
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-20"
          poster="/assets/landing/home/hero.png"
        >
          <source src="/assets/videos/hall-of-fame-hero.mp4" type="video/mp4" />
        </video>

        {/* Animated gradient overlay (always present, provides depth) */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, rgba(33,32,90,0.95) 0%, rgba(130,83,195,0.7) 50%, rgba(33,32,90,0.9) 100%)",
          }}
        />

        {/* Animated floating orbs */}
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full opacity-10 bg-[#B97BFF] blur-3xl animate-pulse" />
        <div
          className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full opacity-10 bg-[#EFAE33] blur-3xl animate-pulse"
          style={{ animationDelay: "1.5s" }}
        />

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center py-24">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm font-semibold px-4 py-2 rounded-full mb-6">
            <span className="w-2 h-2 bg-[#EFAE33] rounded-full animate-pulse" />
            La Plage Meta Verse
          </div>

          <h1 className="font-black text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-tight mb-6">
            Global Scholars{" "}
            <span
              style={{
                backgroundImage: "linear-gradient(90deg, #EFAE33, #B97BFF)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              Hall of Fame
            </span>
          </h1>

          <p className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            Inspiring a culture of digital education and excellence. Discover
            our award categories and the remarkable scholars who have earned
            their place among Africa's best.
          </p>

          {/* Search */}
          <div className="relative max-w-xl mx-auto">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search a hall of fame..."
              className="w-full bg-white/10 backdrop-blur-sm border border-white/30 text-white placeholder:text-white/50 rounded-2xl px-6 py-4 pr-14 text-base focus:outline-none focus:ring-2 focus:ring-[#EFAE33]/50 focus:border-[#EFAE33]/50 transition-all"
            />
            <div className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center justify-center gap-8 mt-10 text-white/80 text-sm">
            <div className="text-center">
              <p className="text-3xl font-black text-white">
                {categories?.length ?? 0}
              </p>
              <p>Award Categories</p>
            </div>
            <div className="w-px h-10 bg-white/20" />
            <div className="text-center">
              <p className="text-3xl font-black text-[#EFAE33]">Global</p>
              <p>Recognition</p>
            </div>
            <div className="w-px h-10 bg-white/20" />
            <div className="text-center">
              <p className="text-3xl font-black text-white">100%</p>
              <p>Excellence</p>
            </div>
          </div>
        </div>

        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg
            viewBox="0 0 1440 60"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 60L60 50C120 40 240 20 360 15C480 10 600 20 720 25C840 30 960 30 1080 25C1200 20 1320 10 1380 5L1440 0V60H0Z"
              fill="white"
            />
          </svg>
        </div>
      </section>

      {/* ═══════════════════════════════════ CATEGORIES ══════════════════════════ */}
      <section className="max-w-7xl mx-auto px-4 py-16 md:py-20">
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="rounded-2xl overflow-hidden animate-pulse"
              >
                <div className="h-32 bg-gray-200" />
                <div className="p-5 bg-white space-y-3">
                  <div className="h-5 bg-gray-200 rounded w-3/4" />
                  <div className="h-4 bg-gray-100 rounded w-1/2" />
                  <div className="h-10 bg-gray-200 rounded-xl" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-20 text-red-500">
            <p className="text-xl font-semibold">
              Failed to load halls of fame
            </p>
            <p className="text-sm mt-2 text-gray-500">
              Please try refreshing the page
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-5xl mb-4">🔍</p>
            <p className="text-xl font-semibold text-gray-700">
              No results for "{searchQuery}"
            </p>
            <p className="text-gray-400 mt-2">Try a different search term</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-bold text-2xl text-gray-900">
                {searchQuery
                  ? `Results for "${searchQuery}"`
                  : "All Halls of Fame"}
              </h2>
              <span className="text-gray-400 text-sm">
                {filtered.length} categor{filtered.length !== 1 ? "ies" : "y"}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((category, index) => (
                <HallOfFameCategoryCard
                  key={category.id}
                  category={category}
                  colorIndex={index}
                />
              ))}
            </div>
          </>
        )}
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#F4F6F9] py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-lg text-gray-600 leading-relaxed mb-3">
            At La Plage Meta Verse, greatness is earned through consistency and
            commitment.
          </p>
          <p className="font-bold text-2xl text-primary mb-8">
            Be a Top Scholar. Be the kind of graduate every employer is looking
            for.
          </p>
          <a
            href="https://student.laplagemetaverse.org/sign-up"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-[#21205A] hover:bg-[#21205A]/90 text-white font-bold px-10 py-4 rounded-xl transition-colors"
          >
            Start Your Journey Today
          </a>
        </div>
      </section>

      <Footer />
    </main>
  );
}
